import { msg } from "./locales/translate";
import { useMemo } from "react";
import { Calculator } from "lucide-react";
import { IconAcquirers } from "./assets/menu/icon_acquirers";
import { IconClients } from "./assets/menu/icon_clients";
import { IconDashboard } from "./assets/menu/icon_dashboard";
import { IconEvents } from "./assets/menu/icon_events";
import { IconPayouts } from "./assets/menu/icon_payouts";
import { IconProcessing } from "./assets/menu/icon_processing";
import { IconReports } from "./assets/menu/icon_reposts";
import { IconUsers } from "./assets/menu/icon_users";
import { GD } from "./GD";
import { INav } from "./services/Router";
import { AcquirerChannelPage } from "./view/pages/AcquirerChannelPage";
import { AcquirerPage } from "./view/pages/AcquirerPage";
import { AcquirersPage } from "./view/pages/AcquirersPage";
import { ClientGroupPage } from "./view/pages/clientGroupPage/ClientGroupPage";
import { MerchantTerminalPage } from "./view/pages/clientGroupPage/subpages/MerchantTerminalPage";
import { ClientPage } from "./view/pages/clientPage/ClientPage";
import { ClientsPage } from "./view/pages/ClientsPage";
import { DashboardPage } from "./view/pages/DashboarPage";
import { EventsPage } from "./view/pages/EventsPage";
import { MerchantChannelPage } from "./view/pages/MerchantChannelPage";
import { PayoutsPage } from "./view/pages/PayoutsPage";
import { ProcessingPage } from "./view/pages/ProcessingPage";
import { ClientRegisterSelector } from "./view/pages/registerPage/ClientRegisterSelector";
import { ReportsPage } from "./view/pages/ReportsPage";
import { UsersPage } from "./view/pages/UsersPage";
import { AccountantPage } from "./view/pages/AccountantPage";
import { AdministrationPage } from "./view/pages/AdministrationPage";
import { IProject } from "./services/Projects";
import { MerchantGeneralPage } from "./view/pages/merchantView/MerchantGeneralPage";
import { RegisterPage } from "./view/pages/registerPage/RegisterPage";
import { AppRole, hasAnyRole, hasPermission, hasRole } from "./permissions";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";

const NAV_LABEL_KEYS: Record<string, string> = {
	dashboard: "nav.dashboard",
	register: "nav.registration",
	registerClient: "nav.registration",
	clients: "nav.clients",
	accountant: "nav.accountant",
	client: "nav.client",
	providers: "nav.providers",
	provider: "nav.provider",
	payouts: "nav.payouts",
	users: "nav.users",
	processing: "nav.processing",
	reports: "nav.reports",
	events: "nav.events",
	administration: "nav.administration",
	merchant: "nav.merchant",
	merchantchannel: "nav.channel",
	providerchannel: "nav.channel",
	merchantterminal: "nav.terminal",
};

const localizeNav = (nodes: INav[]): INav[] => nodes.map(node => ({
	...node,
	label: NAV_LABEL_KEYS[node.id] ? i18n.t(NAV_LABEL_KEYS[node.id]) : node.label,
	sub: node.sub ? localizeNav(node.sub) : undefined,
}));

const merchant_sitemap: INav[] = [
	{
		path: "/",
		get label() { return msg("dashboard"); },
		id: "dashboard",
		roles: ["merchant"],
		icon: <IconDashboard />,
		page: <MerchantGeneralPage />,
	},
	{
		path: "register",
		get label() { return msg("registration"); },
		id: "register",
		roles: ["merchant"],
		// icon: <IconDashboard />,
		page: <ClientRegisterSelector />,
		sub: [
			{
				path: "registerClient/:rid",
				id: "registerClient",
				get label() { return msg("clientRegistration"); },
				page: <RegisterPage />,
			},
		],
	},
	{
		path: "clients",
		get label() { return msg("clients"); },
		roles: ["merchant"],
		id: "clients",
		icon: <IconClients />,
		page: <MerchantGeneralPage />,
		sub: [
			{
				path: ":cid",
				id: "client",
				get label() { return msg("client"); },
				page: <ClientPage />,
				sub: [
					{
						path: "merchant/:mid",
						id: "merchant",
						page: <ClientGroupPage />,
						get label() { return msg("merchant"); },
						sub: [
							{
								path: "channel/:chid",
								id: "merchantchannel",
								roles: ["merchant"],
								page: <MerchantChannelPage />,
								get label() { return msg("channel"); },
							},
							{
								path: "terminal/:tid",
								id: "merchantterminal",
								roles: ["merchant"],
								page: <MerchantTerminalPage />,
								get label() { return msg("terminal"); },
							},
						],
					},
				],
			},
		],
	},
];

const _sitemap: INav[] = [
	{
		path: "/",
		get label() { return msg("dashboard"); },
		id: "dashboard",
		permission: "nav.dashboard",
		icon: <IconDashboard />,
		page: <DashboardPage />,
	},
	{
		path: "register",
		get label() { return msg("registration"); },
		id: "register",
		roles: ["admin"],
		exclude: true,
		// icon: <IconDashboard />,
		page: <ClientRegisterSelector />,
		sub: [
			{
				path: "registerClient/:rid",
				id: "registerClient",
				get label() { return msg("clientRegistration"); },
				page: <RegisterPage />,
			},
		],
	},
	{
		path: "clients",
		get label() { return msg("clients"); },
		id: "clients",
		permission: "nav.clients",
		icon: <IconClients />,
		page: <ClientsPage />,
		sub: [
			{
				path: ":cid",
				id: "client",
				get label() { return msg("client"); },
				page: <ClientPage />,
				sub: [
					{
						path: "merchant/:mid",
						id: "merchant",
						page: <ClientGroupPage />,
						get label() { return msg("merchant"); },
						sub: [
							{
								path: "channel/:chid",
								id: "merchantchannel",
								roles: ["admin", "aml", "merchant"],
								page: <MerchantChannelPage />,
								get label() { return msg("channel"); },
							},
							{
								path: "terminal/:tid",
								id: "merchantterminal",
								roles: ["admin", "aml", "merchant"],
								page: <MerchantTerminalPage />,
								get label() { return msg("terminal"); },
							},
						],
					},
				],
			},
		],
	},
	{
		path: "accountant",
		get label() { return msg("accountant"); },
		id: "accountant",
		roles: ["admin", "accountant"],
		icon: <Calculator size={20} />,
		page: <AccountantPage />,
	},
	{
		path: "providers",
		get label() { return msg("providers"); },
		id: "providers",
		permission: "nav.providers",
		icon: <IconAcquirers />,
		page: <AcquirersPage />,
		sub: [
			{
				path: ":aid",
				id: "provider",
				page: <AcquirerPage />,
				get label() { return msg("paymentProvider"); },
				sub: [
					{
						path: "providerchannel/:cid",
						id: "providerchannel",
						page: <AcquirerChannelPage />,
						get label() { return msg("channel"); },
					},
				],
			},
		],
	},
	// {
	// 	path: "terminals",
	// 	label: "Terminals",
	// 	id: "terminals",
	// 	roles: ["admin", "aml"],
	// 	icon: <IconTerminals />,
	// 	page: <TerminalsPage />,
	// 	sub: [
	// 		{
	// 			path: ":tid",
	// 			id: "terminal",
	// 			page: <TerminalPage />,
	// 			label: "Terminal",
	// 		},
	// 	],
	// },
	// {
	// 	path: "banks",
	// 	label: "Banks",
	// 	id: "banks",
	// 	roles: ["admin"],
	// 	icon: <IconBanks />,
	// 	page: <BanksPage />,
	// },
	{
		path: "payouts",
		get label() { return msg("payouts"); },
		id: "payouts",
		permission: "nav.payouts",
		icon: <IconPayouts />,
		page: <PayoutsPage />,
	},
	{
		path: "users",
		get label() { return msg("users"); },
		id: "users",
		permission: "nav.users",
		icon: <IconUsers />,
		page: <UsersPage />,
	},
	// {
	// 	path: "analytic",
	// 	label: "Analytic",
	// 	id: "analytic",
	// 	roles: ["admin"],
	// 	icon: <IconAnalytic />,
	// 	page: <AnalyticPage />,
	// },
	{
		path: "processing",
		get label() { return msg("processing"); },
		id: "processing",
		permission: "nav.processing",
		icon: <IconProcessing />,
		page: <ProcessingPage />,
	},

	{
		path: "reports",
		get label() { return msg("reports"); },
		id: "reports",
		permission: "nav.reports",
		icon: <IconReports />,
		page: <ReportsPage />,
	},
	{
		path: "events",
		get label() { return msg("events"); },
		id: "events",
		permission: "nav.events",
		icon: <IconEvents />,
		page: <EventsPage />,
	},
	{
		path: "administration",
		get label() { return msg("administration"); },
		id: "administration",
		roles: ["admin"],
		page: <AdministrationPage />,
	},
] as const;

export interface IMenu {
	label: string;
	id: string;
	type: string;
	icon?: JSX.Element;
}

//let _menu: IMenu[] | null = null;
// export const useMenu = () => {
// 	if (_menu) return _menu;
// 	_menu = [];
// 	for (let i of _sitemap) {
// 		_menu.push({
// 			label: i.label,
// 			id: i.id,
// 			icon: i.icon,
// 			type: i.path.startsWith("$") ? i.path.substring(1) : "item",
// 		});
// 	}
// 	return _menu;
// };

export const useMenu = () => {
	const { i18n: activeI18n } = useTranslation();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	const res: IMenu[] | null = useMemo(() => {
		let _menu: IMenu[] | null;
		if (project && user) {
			_menu = [];
			const userProject = user.projects.find(val => val.id === project.id);
			if (userProject) {
				for (const i of _sitemap) {
					const allowed = i.permission ? hasPermission(userProject.roles, i.permission) : !!i.roles && hasAnyRole(userProject.roles, i.roles as AppRole[]);
					if (!i.exclude && allowed) {
						if (userProject.id === 3 && i.id === "register") {
							continue;
						}
						_menu.push({
							label: i.label,
							id: i.id,
							icon: i.icon,
							type: i.path.startsWith("$") ? i.path.substring(1) : "item",
						});
					}
				}
			}

			return _menu;
		}

		return null;
	}, [project, user, activeI18n.resolvedLanguage]);

	return res;
};

const getSitemapByRole = (
	project: IProject,
	user: {
		id: number;
		name: string;
		password: string;
		projects: IProject[];
	},
) => {
	// const [project] = GD.B_SELECTED_PROJECT.use();
	// const [user] = GD.REQ_PROFILE.useRequest();

	// if (!project || !user) {
	// 	return null;
	// }

	let res: INav[] = [];
	res = _sitemap as INav[];
	if (project && user) {
		const userProject = user.projects.find(val => val.id === project.id);

		if (userProject) {
			if (hasRole(userProject.roles, "merchant") && !hasAnyRole(userProject.roles, ["admin", "aml", "accountant", "support"])) {
				res = merchant_sitemap as INav[];
			}

			const filterNodes = (nodes: INav[]): INav[] =>
				nodes.flatMap(node => {
					if (node.permission && !hasPermission(userProject.roles, node.permission)) return [];
					if (!node.permission && node.roles && !hasAnyRole(userProject.roles, node.roles as AppRole[])) return [];
					return [{ ...node, sub: node.sub ? filterNodes(node.sub) : undefined }];
				});

			return filterNodes(res).filter(val => !val.path.startsWith("$"));
		}
	}
	return res.filter(val => !val.path.startsWith("$"));
};

export const sitemap = (
	project: IProject,
	user: {
		id: number;
		name: string;
		password: string;
		projects: IProject[];
	},
): INav[] => localizeNav(getSitemapByRole(project, user));
